import React, { useState, useEffect } from 'react';
import { subscriptionApi } from '../../api/subscriptionApi';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  CheckIcon,
  XMarkIcon,
  SparklesIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const featureLabels = {
  mailbox: 'Mailbox & Email',
  aiAssist: 'AI Assist (Gemini)',
  whatsapp: 'WhatsApp Reminders',
  exportReports: 'Export PDF/Excel',
  gstReports: 'GST Reports',
  invoiceCustomization: 'Invoice Customization',
  scheduledEmails: 'Scheduled Emails',
  customBranding: 'Custom Branding',
  apiAccess: 'API Access',
  multiBranch: 'Multi-branch'
};

const limitLabels = {
  maxCustomers: 'Customers',
  maxTransactions: 'Transactions/mo',
  maxProducts: 'Products',
  maxBusinesses: 'Businesses',
  maxStaff: 'Staff Accounts',
  maxAiPerDay: 'AI Calls/day',
  maxScheduledEmails: 'Scheduled Emails/day'
};

const Pricing = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const currentPlan = user?.subscription?.plan || 'free';

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await subscriptionApi.getPlans();
      if (res.data.success) {
        setPlans(res.data.data);
      }
    } catch (error) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleUpgrade = async (planSlug) => {
    if (planSlug === currentPlan) return;
    setUpgrading(planSlug);
    try {
      // 1. Load Script
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        toast.error('Razorpay SDK failed to load. Are you online?');
        setUpgrading(null);
        return;
      }

      // 2. Create Order
      const res = await subscriptionApi.createRazorpayOrder(planSlug, billingCycle);
      if (!res.data.success) {
        toast.error('Failed to create order');
        setUpgrading(null);
        return;
      }

      const { orderId, amount, currency, keyId } = res.data.data;

      // 3. Open Razorpay Checkout
      const options = {
        key: keyId,
        amount: amount,
        currency: currency,
        name: 'Khatabook Pro',
        description: `Upgrade to ${planSlug.toUpperCase()} Plan`,
        order_id: orderId,
        handler: async function (response) {
          try {
            toast.loading('Verifying payment...', { id: 'verifyPayment' });
            // 4. Verify Payment
            const verifyRes = await subscriptionApi.verifyRazorpayPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              planSlug,
              billingCycle
            });

            if (verifyRes.data.success) {
              toast.success(verifyRes.data.message, { id: 'verifyPayment' });
              setTimeout(() => window.location.reload(), 1500);
            }
          } catch (err) {
            toast.error(err.response?.data?.message || 'Verification failed', { id: 'verifyPayment' });
          }
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
          contact: user?.phone || ''
        },
        theme: {
          color: '#dc343b'
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        toast.error(`Payment Failed: ${response.error.description}`);
      });
      rzp.open();
      
    } catch (error) {
      toast.error(error.response?.data?.message || 'Upgrade failed');
    } finally {
      setUpgrading(null);
    }
  };

  const formatLimit = (val) => {
    if (val === -1) return '∞ Unlimited';
    if (val === 0) return '—';
    return val.toLocaleString('en-IN');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      
      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 px-4 py-1.5 rounded-full mb-4">
          <SparklesIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          <span className="text-[12px] font-bold text-purple-700 dark:text-purple-300 uppercase tracking-wider">Choose Your Plan</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white mb-3">
          Simple, Transparent Pricing
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-[15px] max-w-lg mx-auto">
          Start free and scale as your business grows. No hidden fees. Cancel anytime.
        </p>

        {/* Billing Toggle */}
        <div className="mt-8 inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-5 py-2 rounded-lg text-[13px] font-bold transition-all duration-200 ${
              billingCycle === 'monthly'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-5 py-2 rounded-lg text-[13px] font-bold transition-all duration-200 relative ${
              billingCycle === 'yearly'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
            }`}
          >
            Yearly
            <span className="absolute -top-2 -right-3 bg-green-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
              -20%
            </span>
          </button>
        </div>
      </div>

      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
        {plans.map((plan) => {
          const isCurrentPlan = currentPlan === plan.slug;
          const isBadged = plan.badge?.text;
          const price = billingCycle === 'yearly' ? plan.price.yearly : plan.price.monthly;
          const monthlyEquivalent = billingCycle === 'yearly' ? Math.round(plan.price.yearly / 12) : plan.price.monthly;

          return (
            <div
              key={plan._id}
              className={`relative flex flex-col rounded-2xl border-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                isCurrentPlan
                  ? 'border-[#dc343b] dark:border-red-500 shadow-lg shadow-red-500/10 bg-white dark:bg-gray-900'
                  : isBadged
                    ? 'border-purple-400 dark:border-purple-500 shadow-lg shadow-purple-500/10 bg-white dark:bg-gray-900'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
              }`}
            >
              {/* Badge */}
              {(isBadged || isCurrentPlan) && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider text-white whitespace-nowrap ${
                  isCurrentPlan
                    ? 'bg-[#dc343b]'
                    : `bg-[${plan.badge.color || '#7c3aed'}]`
                }`}
                  style={!isCurrentPlan && isBadged ? { backgroundColor: plan.badge.color } : {}}
                >
                  {isCurrentPlan ? '✓ Current Plan' : plan.badge.text}
                </div>
              )}

              <div className="p-6 flex-1 flex flex-col">
                {/* Plan Name */}
                <h3 className="text-[18px] font-extrabold text-gray-900 dark:text-white mb-1">{plan.name}</h3>
                <p className="text-[12px] text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">{plan.description}</p>

                {/* Price */}
                <div className="mb-6">
                  {price === 0 ? (
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-extrabold text-gray-900 dark:text-white">Free</span>
                      <span className="text-[13px] text-gray-400">forever</span>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className="text-[15px] font-bold text-gray-400">{plan.price.currency}</span>
                      <span className="text-4xl font-extrabold text-gray-900 dark:text-white">{monthlyEquivalent}</span>
                      <span className="text-[13px] text-gray-400">/mo</span>
                    </div>
                  )}
                  {billingCycle === 'yearly' && price > 0 && (
                    <p className="text-[11px] text-green-600 dark:text-green-400 font-semibold mt-1">
                      Billed {plan.price.currency}{price.toLocaleString('en-IN')}/year
                    </p>
                  )}
                </div>

                {/* Limits */}
                <div className="space-y-2 mb-5">
                  {Object.entries(plan.limits || {}).map(([key, val]) => (
                    <div key={key} className="flex items-center justify-between text-[12.5px]">
                      <span className="text-gray-600 dark:text-gray-400">{limitLabels[key] || key}</span>
                      <span className={`font-bold ${val === -1 ? 'text-green-600 dark:text-green-400' : val === 0 ? 'text-gray-300 dark:text-gray-600' : 'text-gray-900 dark:text-white'}`}>
                        {formatLimit(val)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Divider */}
                <div className="h-px bg-gray-100 dark:bg-gray-800 mb-4"></div>

                {/* Features */}
                <div className="space-y-2 flex-1">
                  {Object.entries(plan.features || {}).map(([key, val]) => {
                    if (key === 'autoBackup' || key === 'prioritySupport') return null;
                    return (
                      <div key={key} className="flex items-center gap-2 text-[12.5px]">
                        {val ? (
                          <CheckIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                        ) : (
                          <XMarkIcon className="h-4 w-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                        )}
                        <span className={val ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-600'}>
                          {featureLabels[key] || key}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* CTA Button */}
                <div className="mt-6">
                  <button
                    onClick={() => handleUpgrade(plan.slug)}
                    disabled={isCurrentPlan || upgrading === plan.slug}
                    className={`w-full py-3 rounded-xl text-[14px] font-bold transition-all duration-200 ${
                      isCurrentPlan
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                        : plan.slug === 'premium'
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40'
                          : plan.slug === 'enterprise'
                            ? 'bg-gradient-to-r from-gray-800 to-gray-900 dark:from-gray-600 dark:to-gray-700 text-white hover:shadow-lg'
                            : 'bg-[#dc343b] hover:bg-[#c42a31] text-white shadow-sm hover:shadow-md'
                    }`}
                  >
                    {upgrading === plan.slug ? (
                      <ArrowPathIcon className="h-5 w-5 animate-spin mx-auto" />
                    ) : isCurrentPlan ? (
                      'Current Plan'
                    ) : (
                      `Upgrade to ${plan.name}`
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* FAQ Section */}
      <div className="max-w-2xl mx-auto">
        <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white text-center mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {[
            { q: 'Can I switch plans anytime?', a: 'Yes! You can upgrade or downgrade your plan at any time. When upgrading, the change is immediate. When downgrading, your current features remain until the end of the billing cycle.' },
            { q: 'What happens when I hit my limit?', a: 'You\'ll see a friendly notification suggesting an upgrade. Your existing data is never deleted — you just won\'t be able to add more until you upgrade.' },
            { q: 'Is there a free trial for paid plans?', a: 'Yes! All new signups get a 14-day free trial of the Premium plan. No credit card required.' },
            { q: 'Can I get a refund?', a: 'We offer a 7-day money-back guarantee on all paid plans. If you\'re not satisfied, contact support for a full refund.' },
          ].map((faq, i) => (
            <details key={i} className="group bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-[14px] font-bold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                {faq.q}
                <span className="text-gray-400 group-open:rotate-180 transition-transform duration-200">▼</span>
              </summary>
              <div className="px-5 pb-4 text-[13px] text-gray-600 dark:text-gray-400 leading-relaxed">
                {faq.a}
              </div>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Pricing;
